export const PLANT_REPORT_TYPES = [
  { value: "HUSKER", label: "Husker", hint: "One row per husker — broken %, efficiency." },
  { value: "WHITNER", label: "Whitner", hint: "One row per whitner — broken %, kett." },
  { value: "LENGTH_SCANNER", label: "Length / Scanner", hint: "Flexible line readings (length, grade, notes)." },
  { value: "KETT", label: "Kett", hint: "Kett value + machinery name per row." },
  { value: "DRYER", label: "Dryer", hint: "Broken, crackness, dryer #, lot #." },
  { value: "DYNAMIC", label: "Dynamic", hint: "Any named parameters." },
];

export function defaultPlantRows(type) {
  switch (type) {
    case "HUSKER":
      return [{ husker_label: "", broken: "", efficiency: "" }];
    case "WHITNER":
      return [{ unit_label: "", broken: "", kett: "" }];
    case "LENGTH_SCANNER":
      return [{ length_mm: "", scanner_reading: "", grade: "", remark: "" }];
    case "KETT":
      return [{ kett: "", machinery_name: "" }];
    case "DRYER":
      return [{ broken: "", crackness: "", dryer_number: "", lot_number: "" }];
    case "DYNAMIC":
      return [{ name: "", value: "" }];
    default:
      return [{}];
  }
}

function reqNum(v, label) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) throw new Error(`${label} must be a valid number`);
  return n;
}

function optNum(v) {
  if (v === "" || v == null) return undefined;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
}

export function plantReportTypeLabel(value) {
  return PLANT_REPORT_TYPES.find((t) => t.value === value)?.label ?? String(value || "").replace(/_/g, " ");
}

/** Table shape for displaying saved report payloads in the UI. */
export function getPlantReportTable(reportType, payload) {
  const p = payload || {};
  switch (reportType) {
    case "HUSKER":
      return {
        columns: [
          { key: "husker_label", label: "Husker" },
          { key: "broken", label: "Broken %", format: "num" },
          { key: "efficiency", label: "Efficiency %", format: "num" },
        ],
        rows: p.huskers || [],
      };
    case "WHITNER":
      return {
        columns: [
          { key: "unit_label", label: "Unit" },
          { key: "broken", label: "Broken %", format: "num" },
          { key: "kett", label: "Kett", format: "num" },
        ],
        rows: p.whitners || [],
      };
    case "LENGTH_SCANNER":
      return {
        columns: [
          { key: "length_mm", label: "Length (mm)", format: "num" },
          { key: "scanner_reading", label: "Scanner" },
          { key: "grade", label: "Grade" },
          { key: "remark", label: "Remark" },
        ],
        rows: p.rows || [],
      };
    case "KETT":
      return {
        columns: [
          { key: "machinery_name", label: "Machinery" },
          { key: "kett", label: "Kett", format: "num" },
        ],
        rows: p.entries || [],
      };
    case "DRYER":
      return {
        columns: [
          { key: "dryer_number", label: "Dryer #" },
          { key: "lot_number", label: "Lot #" },
          { key: "broken", label: "Broken %", format: "num" },
          { key: "crackness", label: "Crackness %", format: "num" },
        ],
        rows: p.entries || [],
      };
    case "DYNAMIC":
      return {
        columns: [
          { key: "name", label: "Parameter" },
          { key: "value", label: "Value" },
        ],
        rows: p.parameters || [],
      };
    default:
      return { columns: [], rows: [] };
  }
}

export function formatPlantCell(value, format) {
  if (value === undefined || value === null || value === "") return "—";
  if (format === "num" && typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  return String(value);
}

export function buildPlantPayload(type, rows) {
  switch (type) {
    case "HUSKER":
      return {
        huskers: rows.map((r, i) => ({
          husker_label: r.husker_label || undefined,
          broken: reqNum(r.broken, `Husker row ${i + 1} broken`),
          efficiency: reqNum(r.efficiency, `Husker row ${i + 1} efficiency`),
        })),
      };
    case "WHITNER":
      return {
        whitners: rows.map((r, i) => ({
          unit_label: r.unit_label || undefined,
          broken: reqNum(r.broken, `Whitner row ${i + 1} broken`),
          kett: reqNum(r.kett, `Whitner row ${i + 1} kett`),
        })),
      };
    case "LENGTH_SCANNER":
      return {
        rows: rows.map((r) => ({
          length_mm: optNum(r.length_mm),
          scanner_reading: r.scanner_reading || undefined,
          grade: r.grade || undefined,
          remark: r.remark || undefined,
        })),
      };
    case "KETT":
      return {
        entries: rows.map((r, i) => ({
          kett: reqNum(r.kett, `Kett row ${i + 1} kett`),
          machinery_name: String(r.machinery_name || "").trim(),
        })),
      };
    case "DRYER":
      return {
        entries: rows.map((r, i) => ({
          broken: reqNum(r.broken, `Dryer row ${i + 1} broken`),
          crackness: reqNum(r.crackness, `Dryer row ${i + 1} crackness`),
          dryer_number: String(r.dryer_number || "").trim(),
          lot_number: String(r.lot_number || "").trim(),
        })),
      };
    case "DYNAMIC":
      return {
        parameters: rows.map((r, i) => {
          const name = String(r.name || "").trim();
          if (!name) throw new Error(`Dynamic row ${i + 1}: parameter name required`);
          return { name, value: String(r.value ?? "") };
        }),
      };
    default:
      return {};
  }
}
